import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Check, Settings2 } from "lucide-react";
import { updateGoogleConsent } from "../lib/consent";

const KEY = "voamada_cookie_consent_v1";

// Émet un événement custom pour permettre à d'autres modules (ex: AdSense personnalisation) de réagir
const persist = (value) => {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...value, ts: Date.now() }));
  } catch { /* localStorage indisponible (mode privé) */ }
  // Sync Google Consent Mode v2 (gtag('consent','update',...))
  updateGoogleConsent({ stats: !!value.stats, ads: !!value.ads });
  window.dispatchEvent(new CustomEvent("voamada:cookie-consent", { detail: value }));
};

export const getConsent = () => {
  try {
    const v = localStorage.getItem(KEY);
    return v ? JSON.parse(v) : null;
  } catch { return null; }
};

export const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [stats, setStats] = useState(true);
  const [ads, setAds] = useState(true);

  useEffect(() => {
    const v = getConsent();
    if (!v) {
      // Léger délai pour ne pas masquer le 1er paint
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  // Permet à un lien dans le footer de rouvrir la bannière
  useEffect(() => {
    const handler = () => { setDetails(true); setOpen(true); };
    window.addEventListener("voamada:open-cookie-prefs", handler);
    return () => window.removeEventListener("voamada:open-cookie-prefs", handler);
  }, []);

  const acceptAll = () => { persist({ essential: true, stats: true, ads: true }); setOpen(false); };
  const refuseAll = () => { persist({ essential: true, stats: false, ads: false }); setOpen(false); };
  const savePrefs = () => { persist({ essential: true, stats, ads }); setOpen(false); };

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9999] p-3 md:p-5 pointer-events-none" data-testid="cookie-consent-banner">
      <div className="pointer-events-auto max-w-4xl mx-auto rounded-2xl border border-white/10 bg-[#0F0C0B]/95 backdrop-blur-xl shadow-2xl p-5 md:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-brand-yellow/20 to-brand-orange/20 text-brand-yellow flex items-center justify-center">
            <Cookie size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg md:text-xl font-black mb-1">On respecte ta vie privée 🍪</h2>
            <p className="text-sm text-white/70 leading-relaxed">
              Voamada utilise des cookies pour faire fonctionner le site, comprendre comment tu l'utilises et financer
              le service via la publicité. Tu peux tout accepter, tout refuser ou choisir au cas par cas.
              <Link to="/cookies" className="text-brand-orange hover:underline ml-1">En savoir plus</Link>.
            </p>
          </div>
          <button onClick={refuseAll} className="text-white/40 hover:text-white shrink-0 -m-1 p-1" aria-label="Fermer (refuser)">
            <X size={18} />
          </button>
        </div>

        {details && (
          <div className="space-y-2 mb-4 rounded-xl border border-white/10 bg-black/30 p-4" data-testid="cookie-details">
            <Toggle locked label="Essentiels" desc="Connexion, langue, sécurité — indispensables au fonctionnement." />
            <Toggle checked={stats} onChange={setStats} label="Statistiques anonymes" desc="Comprendre l'usage du site et l'améliorer." testid="consent-stats" />
            <Toggle checked={ads} onChange={setAds} label="Publicité (Google AdSense)" desc="Annonces personnalisées pour financer la plateforme." testid="consent-ads" />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button onClick={acceptAll} className="btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="cookie-accept-all">
            <Check size={14} /> Tout accepter
          </button>
          <button onClick={refuseAll} className="btn-ghost inline-flex items-center gap-1.5 text-sm" data-testid="cookie-refuse-all">
            Tout refuser
          </button>
          {!details ? (
            <button onClick={() => setDetails(true)} className="ml-auto inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white" data-testid="cookie-customize">
              <Settings2 size={12} /> Personnaliser
            </button>
          ) : (
            <button onClick={savePrefs} className="ml-auto btn-ghost inline-flex items-center gap-1.5 text-sm" data-testid="cookie-save-prefs">
              <Check size={14} /> Enregistrer mes choix
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Toggle = ({ checked, onChange, label, desc, locked, testid }) => (
  <label className={`flex items-start gap-3 p-2 rounded-lg ${locked ? "" : "hover:bg-white/[0.02] cursor-pointer"}`}>
    <span className="relative inline-flex items-center mt-0.5 shrink-0">
      <input
        type="checkbox"
        checked={!!checked || !!locked}
        onChange={(e) => !locked && onChange?.(e.target.checked)}
        disabled={!!locked}
        className="sr-only peer"
        data-testid={testid}
      />
      <span className={`w-9 h-5 rounded-full transition-colors ${checked || locked ? "bg-brand-orange" : "bg-white/15"}`} />
      <span className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked || locked ? "translate-x-4" : ""}`} />
    </span>
    <span className="flex-1 min-w-0">
      <span className="text-sm font-bold flex items-center gap-2">
        {label}
        {locked && <span className="text-[9px] uppercase tracking-widest text-emerald-400 font-bold">Toujours actif</span>}
      </span>
      <span className="block text-xs text-white/55 mt-0.5">{desc}</span>
    </span>
  </label>
);

export default CookieConsent;
